import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../store/user.store";
import { getLocalDateId } from "../services/admin.service";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  limit,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { DailyUserTiffin, MealType } from "../types/tiffin";
import { Spinner } from "../components/common/Spinner";
import { Button } from "../components/common/Button";
import { Card } from "../components/common/Card";
import { toast } from "react-hot-toast";
import { ArrowLeft, Calendar as CalendarIcon } from "lucide-react";
import { CartItem } from "../types/index";
import { COLLECTIONS } from "../constants/collections";

const DAILY = COLLECTIONS.DAILY_TIFFINS; // canonical: daily_tiffins

/** Resolve today's daily_tiffins doc by userId+date (supports auto-id and userId_date). */
const getDailyTiffin = async (
  userId: string,
  dateId: string
): Promise<(DailyUserTiffin & { id: string }) | null> => {
  // Prefer query so both auto-id (signup) and `${userId}_${date}` (preGenerate/migration) work
  const q = query(
    collection(db, DAILY),
    where("userId", "==", userId),
    where("date", "==", dateId),
    limit(5)
  );
  const snap = await getDocs(q);
  if (!snap.empty) {
    // Prefer scheduled over skipped/cancelled if duplicates somehow exist
    const docs = snap.docs.map((d) => ({ ...(d.data() as DailyUserTiffin), id: d.id }));
    const scheduled = docs.find((d) => d.status === "scheduled");
    return scheduled || docs[0];
  }

  // Fallback: legacy doc id pattern
  const legacyRef = doc(db, DAILY, `${userId}_${dateId}`);
  const legacySnap = await getDoc(legacyRef);
  if (legacySnap.exists()) {
    return { ...(legacySnap.data() as DailyUserTiffin), id: legacySnap.id };
  }
  return null;
};

const updateDailyTiffinItems = async (
  docId: string,
  items: CartItem[],
  totalAmount: number
) => {
  const tiffinRef = doc(db, DAILY, docId);
  await updateDoc(tiffinRef, { items, totalAmount, isCustomized: true });
};

const cancelDailyTiffin = async (docId: string, meal: MealType) => {
  const tiffinRef = doc(db, DAILY, docId);
  const tiffinSnap = await getDoc(tiffinRef);
  if (!tiffinSnap.exists()) return;

  const tiffin = tiffinSnap.data() as DailyUserTiffin;

  const newItems = tiffin.items.filter(
    (item) =>
      (item.category as string).toLowerCase() !== meal.toLowerCase()
  );
  const newTotal = newItems.reduce(
    (acc, item) => acc + (item.price || 0) * item.quantity,
    0
  );
  const newMeals = tiffin.meals.filter((m) => m !== meal);

  await updateDoc(tiffinRef, {
    items: newItems,
    totalAmount: newTotal,
    meals: newMeals,
    ...(newMeals.length === 0
      ? { status: "cancelled_by_user" }
      : { status: "scheduled" }),
  });
};

// Must match the server meal-lock times (functions/src/subscriptionMealCore.ts MEAL_LOCK_HOUR_IST):
// breakfast 6 AM (edits close at 5 AM, as in the planner), lunch 10 AM, evening snacks 3 PM, dinner 6 PM.
const getCutoffTime = (meal: MealType): Date => {
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (meal) {
    case "morning":
    case "breakfast":
      cutoff.setHours(5, 0, 0, 0);
      break;
    case "afternoon":
    case "lunch":
      cutoff.setHours(10, 0, 0, 0);
      break;
    case "evening_snacks":
      cutoff.setHours(15, 0, 0, 0);
      break;
    case "night":
    case "dinner":
      cutoff.setHours(18, 0, 0, 0);
      break;
    default:
      cutoff.setHours(23, 59, 0, 0);
  }
  return cutoff;
};

const MyDailyTiffinPage: React.FC = () => {
  const { userProfile } = useUserStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [todayId] = useState(() => getLocalDateId(new Date()));
  const [now, setNow] = useState(new Date());

  const queryKey = ["dailyTiffin", userProfile?.uid, todayId];

  const { data: tiffin, isLoading } = useQuery(
    queryKey,
    () => getDailyTiffin(userProfile!.uid, todayId),
    {
      enabled: !!userProfile?.uid && !!todayId,
      refetchOnWindowFocus: true,
    }
  );

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const itemsByMeal = useMemo(() => {
    const groups: Partial<Record<MealType, CartItem[]>> = {};

    if (tiffin) {
      tiffin.items.forEach((item) => {
        const meal = (item.category as string).toLowerCase() as MealType;
        if (!groups[meal]) groups[meal] = [];
        groups[meal]!.push(item);
      });
    }
    return groups;
  }, [tiffin]);

  const { mutate: updateItems, isLoading: isUpdating } = useMutation(
    (newItems: CartItem[]) => {
      if (!tiffin?.id) throw new Error("No tiffin doc");
      const newTotal = newItems.reduce(
        (acc, item) => acc + (item.price || 0) * item.quantity,
        0
      );
      return updateDailyTiffinItems(tiffin.id, newItems, newTotal);
    },
    {
      onSuccess: () => {
        toast.success("Tiffin updated!");
        queryClient.invalidateQueries(queryKey);
      },
      onError: (err: any) => toast.error(err.message || "Failed to update."),
    }
  );

  const { mutate: cancelMeal, isLoading: isCancelling } = useMutation(
    (meal: MealType) => {
      if (!tiffin?.id) throw new Error("No tiffin doc");
      return cancelDailyTiffin(tiffin.id, meal);
    },
    {
      onSuccess: (_data, meal) => {
        toast.success(`${meal.replace("_", " ")} tiffin cancelled for today.`);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (err: any) => toast.error(err.message || "Failed to cancel."),
    }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size={48} />
        <p className="ml-4 text-gray-500">Loading today's tiffin...</p>
      </div>
    );
  }

  if (!tiffin || tiffin.items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="max-w-2xl mx-auto p-8">
          <button
            onClick={() => navigate("/my-tiffin")}
            className="text-sm text-gray-500 hover:text-orange-600 mb-6 flex items-center gap-2 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Full Calendar
          </button>

          <Card className="p-12 text-center">
            <CalendarIcon className="mx-auto text-gray-300 mb-4" size={64} />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              No Tiffin for Today
            </h1>
            <p className="text-gray-600">
              You have no tiffin scheduled for today.
            </p>
            <Button onClick={() => navigate("/subscription")} className="mt-6">
              Create Subscription
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  if (tiffin.status === "ordered" || tiffin.status === "locked") {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="max-w-2xl mx-auto p-8">
          <button
            onClick={() => navigate("/my-tiffin")}
            className="text-sm text-gray-500 hover:text-orange-600 mb-6 flex items-center gap-2 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Full Calendar
          </button>

          <h1 className="text-2xl font-bold mb-6">
            My Tiffin for Today ({todayId})
          </h1>
          <Card className="bg-green-50 border-green-200 p-6 text-center">
            <p className="font-semibold text-green-700 text-lg mb-2">
              ✅ Your order has been placed!
            </p>
            <p className="text-sm text-gray-600">
              You can track it in your Order History.
            </p>
            <Button onClick={() => navigate("/orders")} className="mt-4">
              View Orders
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  if (
    tiffin.status === "cancelled_by_user" ||
    tiffin.status === "skipped" ||
    tiffin.status === "cancelled"
  ) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="max-w-2xl mx-auto p-8">
          <button
            onClick={() => navigate("/my-tiffin")}
            className="text-sm text-gray-500 hover:text-orange-600 mb-6 flex items-center gap-2 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Full Calendar
          </button>

          <h1 className="text-2xl font-bold mb-6">
            My Tiffin for Today ({todayId})
          </h1>
          <Card className="bg-red-50 border-red-200 p-6 text-center">
            <p className="font-semibold text-red-700 text-lg">
              ❌ Your tiffin for today was cancelled / skipped.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-2xl mx-auto p-8 space-y-6">
        <button
          onClick={() => navigate("/my-tiffin")}
          className="text-sm text-gray-500 hover:text-orange-600 mb-4 flex items-center gap-2 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Full Calendar
        </button>

        <h1 className="text-2xl font-bold">My Tiffin for Today ({todayId})</h1>

        {(Object.keys(itemsByMeal) as MealType[]).map((meal) => {
          const items = itemsByMeal[meal];
          if (!items || items.length === 0) return null;

          const cutoffTime = getCutoffTime(meal);
          const isPastCutoff = now > cutoffTime;
          const canModify = !isPastCutoff;

          const mealTotal = items.reduce(
            (acc, item) => acc + (item.price || 0) * item.quantity,
            0
          );

          return (
            <Card
              key={meal}
              className={!canModify ? "opacity-70 bg-gray-50" : ""}
            >
              <div className="flex justify-between items-center mb-4 pb-2 border-b">
                <h2 className="text-xl font-semibold capitalize">
                  {meal.replace("_", " ")}
                </h2>
                {isPastCutoff ? (
                  <span className="text-sm font-medium text-red-500">
                    Cutoff passed (
                    {cutoffTime.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    )
                  </span>
                ) : (
                  <span className="text-sm font-medium text-green-600">
                    Cutoff:{" "}
                    {cutoffTime.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                )}
              </div>

              <div className="space-y-3">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex justify-between items-center p-3 bg-gray-50 rounded"
                  >
                    <div>
                      <p className="font-medium">
                        {item.name} (x{item.quantity})
                      </p>
                      <p className="text-sm text-gray-500">
                        From: {item.kitchenName}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">
                        ₹{((item.price || 0) * item.quantity).toFixed(2)}
                      </span>
                      <Button
                        variant="danger"
                        className="px-2 py-1 text-xs"
                        onClick={() => {
                          const newItems = tiffin.items.filter(
                            (i) => i.id !== item.id
                          );
                          updateItems(newItems);
                        }}
                        disabled={!canModify || isUpdating}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}

                <Button variant="secondary" disabled={!canModify}>
                  Change Item (Coming Soon)
                </Button>
              </div>

              <div className="border-t mt-4 pt-4 space-y-3">
                <div className="flex justify-between font-bold text-lg">
                  <span>Subtotal:</span>
                  <span>₹{mealTotal.toFixed(2)}</span>
                </div>
                <Button
                  variant="danger"
                  className="w-full"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Are you sure you want to cancel ${meal.replace(
                          "_",
                          " "
                        )} for today?`
                      )
                    ) {
                      cancelMeal(meal);
                    }
                  }}
                  isLoading={isCancelling}
                  disabled={!canModify}
                >
                  Cancel {meal.replace("_", " ")}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default MyDailyTiffinPage;
